/**
 * Guest facts for the Rocha Brava tab.
 * Each line is taken from the resort’s own pages. Anything those pages do not
 * state is marked “confirm at reception” instead of guessed.
 *
 * Sources:
 * - https://rochabrava.com/en/resort-in-carvoeiro/
 * - https://rochabrava.com/en/contact-us/
 * - https://rochabrava.com/en/accommodation/1-bedroom-sea-view-apartments-for-holidays-in-the-algarve/
 * - https://rochabrava.com/en/restaurants-in-carvoeiro-where-to-eat/
 * - https://rochabrava.com/resort-no-carvoeiro/ (Portuguese; café hours and tennis phone agree)
 */

export interface ResortLine {
  title: string;
  body: string;
  /** Show an English gloss under a Portuguese name. */
  gloss?: boolean;
  href?: string;
}

export const RESORT_SOURCES: { href: string; label: string }[] = [
  { href: 'https://rochabrava.com/en/resort-in-carvoeiro/', label: 'Resort guide' },
  { href: 'https://rochabrava.com/en/contact-us/', label: 'Contact' },
  {
    href: 'https://rochabrava.com/en/accommodation/1-bedroom-sea-view-apartments-for-holidays-in-the-algarve/',
    label: 'Apartment terms',
  },
  { href: 'https://rochabrava.com/en/restaurants-in-carvoeiro-where-to-eat/', label: 'O Farol meals' },
];

export const RESORT_DINING: { name: string; detail: string }[] = [
  {
    name: 'O Farol',
    detail: 'Portuguese and international food, breakfast, lunch, and dinner, year-round. Meal times: confirm at reception.',
  },
  { name: 'Café e Bar', detail: 'Open daily, 9:00 AM–11:00 PM.' },
];

export const RESORT_DINING_SOURCE = 'https://rochabrava.com/en/resort-in-carvoeiro/';

export const RESORT_OVERVIEW: ResortLine[] = [
  {
    title: 'The resort',
    body: '23 hectares (about 57 acres) in Carvoeiro, overlooking the sea. Family facilities, gardens, and a 24-hour reception.',
  },
  {
    title: 'Check-in and check-out',
    body: 'Check-in after 3:30 PM. Check-out by 12:00 PM. Published on the apartment booking terms.',
  },
];

export const RESORT_OVERVIEW_MORE: ResortLine[] = [
  {
    title: 'Direct booking',
    body: 'A booking on the resort website, by phone, or by email includes complimentary gym access and one free round of mini golf.',
  },
  {
    title: 'If you cancel',
    body: '30% of the cost is charged when you book. 100% is charged from 3:30 PM on the arrival day, or for a no-show.',
  },
];

export const RESORT_POOLS: ResortLine[] = [
  {
    title: 'Outdoor pools',
    body: 'Three outdoor pools, for a break from the sea. Towel rental is available.',
  },
  {
    title: 'Heated covered pool',
    body: 'Open from 1 October to 30 April.',
  },
];

export const RESORT_POOLS_MORE: ResortLine[] = [
  {
    title: 'Country-village pool',
    body: 'A heated pool in the country village runs from Easter to the end of May, and from 1 October to mid-November.',
  },
];

export const RESORT_BEACH: ResortLine[] = [
  {
    title: 'Praia do Carvoeiro',
    gloss: true,
    href: '/places/praia-do-carvoeiro',
    body: 'Free shuttle from the resort, three times a day. The timetable is not published. Confirm at reception.',
  },
];

export const RESORT_BEACH_MORE: ResortLine[] = [
  {
    title: 'Praia de Vale Covo',
    gloss: true,
    href: '/places/praia-vale-covo',
    body: 'The cove at the resort. The resort site does not describe the path down. Confirm at reception.',
  },
  {
    title: 'Algar Seco',
    gloss: true,
    href: '/places/algar-seco-boardwalk',
    body: 'Cliff boardwalk at the edge of the village, toward Vale Centeanes. Walking directions from the villa are not published. Confirm at reception.',
  },
  {
    title: 'Praia de Vale Centeanes',
    gloss: true,
    href: '/places/praia-vale-centeanes',
    body: 'The next beach east of the boardwalk. How to walk there from the resort: confirm at reception.',
  },
];

export const RESORT_DINING_MORE: ResortLine[] = [
  {
    title: 'Café e Bar',
    gloss: true,
    body: 'Next to the main entrance. Snacks, continental and full English breakfast, crepes, ice cream, and the bar. Free fibre-optic Wi-Fi and satellite TV.',
  },
  {
    title: 'Supermarket',
    body: 'On site, open daily until 9:00 PM. The morning opening time is not published. Confirm at reception. Closed 25 December and 1 January. Reopens at 1:00 PM on 26 December and 2 January.',
  },
];

export const RESORT_PRACTICAL: ResortLine[] = [
  {
    title: 'Wi-Fi',
    body: 'Free fibre-optic Wi-Fi in the accommodation and at the Café e Bar.',
  },
  {
    title: 'Housekeeping',
    body: 'Cleaning every other day. An iron and ironing board are in the apartment.',
  },
];

export const RESORT_PRACTICAL_MORE: ResortLine[] = [
  {
    title: 'Laundry',
    body: 'A laundry room is on site. Opening hours are not published. Confirm at reception.',
  },
  {
    title: 'Parking',
    body: 'Parking is listed for the apartments and villas. The bay, and any fee, are not published. Confirm at reception.',
  },
  {
    title: 'Rules',
    body: 'Accommodation is listed as non-smoking. A safe can be hired. A cot is available.',
  },
];

export const RESORT_ACTIVITIES: ResortLine[] = [
  {
    title: 'Tennis and pickleball',
    body: 'Four courts with a slip-resistant surface. Book at the Tennis Club reception, +351 911 740 098 or club@rochabravarc.com. Equipment rental and lessons are offered.',
  },
];

export const RESORT_ACTIVITIES_MORE: ResortLine[] = [
  {
    title: 'Gym',
    body: 'Weights, rowing machines, bikes, treadmills, benches, and ellipticals, with air conditioning, showers, and changing rooms. Pilates and Tai Chi, plus more classes in summer. A personal trainer needs 24 hours’ notice. Gym hours are not published. Confirm at reception. Complimentary for a direct booking.',
  },
  {
    title: 'Kids',
    body: 'Kids Club is free for guests aged 4 to 12, from April to the end of October, Monday to Friday, 9:00 AM–1:00 PM and 2:00 PM–6:00 PM, near O Farol. There is also an outdoor playground with swings.',
  },
  {
    title: 'Mini golf and table tennis',
    body: 'Mini golf is from €5 for 2 hours, plus a €20 equipment deposit. One round is free on a direct booking. Two table-tennis tables; bats are free against a deposit.',
  },
  {
    title: 'Also on site',
    body: 'An outdoor exercise circuit and a boccia court. The beauty centre is open Monday to Saturday, 9:00 AM–6:00 PM, on +351 965 138 131.',
  },
];

export const RESORT_HEALTH: ResortLine[] = [
  {
    title: 'Farmácia Rocha Brava',
    gloss: true,
    href: '/places/farmacia-rocha-brava',
    body: 'Pharmacy at the resort. Opening hours are not on the resort site. Confirm at reception.',
  },
];

export const RESORT_HEALTH_MORE: ResortLine[] = [
  {
    title: 'Farmácia Carvoeiro',
    gloss: true,
    href: '/places/farmacia-carvoeiro',
    body: 'Pharmacy in the village, on the lighthouse road.',
  },
  {
    title: 'Centro de Saúde de Lagoa',
    gloss: true,
    href: '/places/centro-saude-lagoa',
    body: 'Public health centre in Lagoa. For an emergency, call 112 or go to Hospital de Portimão.',
  },
  {
    title: 'Taxis',
    body: 'The resort does not publish a taxi number. Confirm at reception. The resort can also book a transfer.',
  },
];
